/**
 * 多图参考卡(W2-B9,2026-09-25 模特试戴)——「参考图(可选,最多 4 张)」。
 *
 * 上传 / 从产品库选,每张选用途(产品 / 模特 / 场景 / 风格)。提交时随 createTask 发
 * `refImageRefs`(见 model/free-create/multiRefImages.ts 头注的三仓契约)。
 *
 * ★只收 1K:保真参数只在 1K 真送达,⚙ 参数里 2K/4K 会置灰,这里也明说。
 * ★不另计费:报价就是 1K 单价 × 张数,参考图张数不进价。
 * ★上传 / 产品库复制都走后端(产品图走存储内复制,页面 CSP 不许前端 fetch 存储域)。
 */
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ImagePlus, Loader2, X } from "lucide-react";

import {
  PRODUCT_LOAD_FAILED_TRIGGER,
  productService,
  ProductPickSheet,
  type ProductNode,
} from "@/entities/product";
import { toast } from "@/shared/hooks/use-toast";
import { IMAGE_ACCEPT } from "@/shared/lib/uploadImageFormats";
import { Button } from "@/shared/ui/button";
import { PosterCard, PosterCardHeader, PosterSectionTitle } from "@/shared/ui/PosterCard";

import { posterService } from "../../../api/posterService";
import {
  MAX_REF_IMAGES,
  REF_IMAGE_ROLES,
  addRefImage,
  canAddRefImage,
  defaultRoleFor,
  isRefImageRole,
  removeRefImage,
  setRefImageRole,
  type MultiRefImage,
  type RefImageRole,
} from "../../../model/free-create/multiRefImages";
import {
  freeCreateProductFromNode,
  pickableProducts,
} from "../../../model/free-create/productAdapter";
import { errorToast } from "@/shared/lib/apiErrors";

type Adopted = { objectKey: string; tempUrl: string };

interface Props {
  images: MultiRefImage[];
  onChange: (next: MultiRefImage[]) => void;
  disabled?: boolean;
  /** 当前形态不支持参考图时的原因(组图 / 套图);非空 = 卡片灰显、提交不带。 */
  blockedReason?: string | null;
  /** 当前出图引擎不支持多图参考(能力表 supportsMultiRef 不为真)。 */
  engineUnsupported?: boolean;
  /**
   * 页面上方已经有一张「要改的图」(图改图 / 商品主图带实拍)。
   * ★1020:两个框原来都叫「参考图」,用户分不清哪张是被改的。有底图时这张卡改叫「再加参考图」,
   *   并说清它和上面那张的分工;底图是产品实拍时,新图默认用途从「模特」起。
   */
  base?: "none" | "upload" | "product";
  /** 测试注入口;缺省走 posterService。 */
  uploadFile?: (file: File) => Promise<Adopted>;
  adoptProductImage?: (productCode: string) => Promise<Adopted>;
}

export function MultiRefImagesCard({
  images,
  onChange,
  disabled,
  blockedReason,
  engineUnsupported,
  base = "none",
  uploadFile = posterService.uploadRefImage,
  adoptProductImage = (code) => posterService.adoptProductImageAsRef(code, 0),
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  // 产品树懒加载:点开抽屉才拉(与 ProductPickerDrawer 同一个 queryKey,拉过就走缓存)
  const [pickerOpened, setPickerOpened] = useState(false);
  const {
    data: nodes = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["tenant-products"],
    queryFn: () => productService.list(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: pickerOpened,
  });
  const products = useMemo(() => pickableProducts(nodes), [nodes]);

  const full = !canAddRefImage(images);
  const locked = !!disabled || !!blockedReason || busy;

  const append = async (load: () => Promise<Adopted>, role: RefImageRole) => {
    setBusy(true);
    try {
      const res = await load();
      onChange(addRefImage(images, { objectKey: res.objectKey, previewUrl: res.tempUrl, role }));
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "参考图没能加上", hiddenDescription: "请重试一次" }));
    } finally {
      setBusy(false);
    }
  };

  const onPickFile = (file: File | undefined) => {
    if (!file || full) return;
    void append(
      () => uploadFile(file),
      defaultRoleFor(images, { baseIsProduct: base === "product" })
    );
  };

  const onPickProduct = (node: ProductNode | null) => {
    if (!node || full) return;
    if (freeCreateProductFromNode(node, nodes).sampleImages.length === 0) {
      toast({
        title: `「${node.name}」还没传实拍图`,
        description: "先去产品库给它补一张图,或直接上传图片。",
        variant: "destructive",
      });
      return;
    }
    void append(() => adoptProductImage(node.code), "product");
  };

  return (
    <PosterCard>
      <PosterCardHeader>
        <PosterSectionTitle
          title={
            base === "none"
              ? `参考图(可选,最多 ${MAX_REF_IMAGES} 张)`
              : `再加参考图(可选,最多 ${MAX_REF_IMAGES} 张)`
          }
          hint={
            base === "product"
              ? "上面已用商品实拍当底;再放一张模特照,就能让模特戴上 / 用上它。也可放场景、风格图。参考图不另收费"
              : base === "upload"
                ? "上面那张是要改的图;这里放给它参照的图 —— 比如一张模特照,就能让模特戴上 / 用上你的产品。参考图不另收费"
                : "放产品图 + 模特图,就能让模特戴上 / 用上你的产品。参考图不另收费"
          }
        />
      </PosterCardHeader>
      <div className="space-y-2 px-5 pb-5">
        {blockedReason && (
          <p className="text-[12.5px] text-amber-600 dark:text-amber-500">{blockedReason}</p>
        )}
        {!blockedReason && engineUnsupported && images.length > 0 && (
          <p className="text-[12.5px] text-amber-600 dark:text-amber-500">
            当前出图档位不支持多图参考 —— 请在 ⚙ 参数里换成标准 / 高清 / 旗舰 /
            精绘档,或移除参考图。
          </p>
        )}
        {images.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {images.map((img, i) => (
              <li
                key={`${img.objectKey}-${i}`}
                className="flex w-[120px] flex-col gap-1 rounded-lg border border-border/60 bg-background/40 p-1.5"
              >
                <div className="relative">
                  <img
                    src={img.previewUrl}
                    alt={`第 ${i + 1} 张参考图`}
                    className="h-20 w-full rounded-md object-cover"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute right-1 top-1 h-6 w-6 cursor-pointer"
                    aria-label={`移除第 ${i + 1} 张参考图`}
                    disabled={!!disabled || busy}
                    onClick={() => onChange(removeRefImage(images, i))}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <select
                  aria-label={`第 ${i + 1} 张参考图的用途`}
                  value={img.role}
                  disabled={!!disabled || busy}
                  title={REF_IMAGE_ROLES.find((r) => r.value === img.role)?.hint}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (isRefImageRole(v)) onChange(setRefImageRole(images, i, v));
                  }}
                  className="h-7 rounded-md border border-border/60 bg-background px-1 text-[12.5px]"
                >
                  {REF_IMAGE_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          aria-label="上传参考图"
          onChange={(e) => {
            onPickFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        {full ? (
          <p className="text-[12.5px] text-muted-foreground">
            已放满 {MAX_REF_IMAGES} 张,要换先移除一张。
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1 text-[13px]"
              disabled={locked}
              onClick={() => inputRef.current?.click()}
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ImagePlus className="h-3.5 w-3.5" />
              )}
              上传图片
            </Button>
            {/* ★按钮不叫「从产品库选」:那是「带产品资料进指令」的入口,文生图页面刻意不给
                (FreeCreateBody.entries.test 锁着)。这里只取一张产品实拍图当参考,不带资料。 */}
            <ProductPickSheet
              products={products}
              allNodes={nodes}
              onPick={onPickProduct}
              disabled={locked}
              loading={isLoading}
              // 0926 修复波 P11:失败≠空库 —— 抽屉里给「重试」,按钮上也说没读到
              error={isError}
              onRetry={() => void refetch()}
              triggerLabel={
                isError && products.length === 0 ? PRODUCT_LOAD_FAILED_TRIGGER : "选一张产品图"
              }
              onOpen={() => setPickerOpened(true)}
            />
          </div>
        )}
      </div>
    </PosterCard>
  );
}
